import { useStore } from 'hooks/useStore';
import { SiteTransferInfo } from 'Interfaces/BDPortalInterfaces';
import { useEffect, useState } from 'react';
import { deepCopy } from 'utils/deepCopy';

const SiteTransferLogic = (opportunityIterationID: number, closeModal: () => void) => {
  const {
    UserStore: { userModulesRoles },
    BDPortalStore: {
      siteTransferLoading,
      siteTransferNotes,
      saveSiteTransferDetails,
      siteTransferNoteLoading,
      getSiteTransfer,
      saveSiteTransferNote,
    },
  } = useStore();
  const [note, setNote] = useState<string>('');
  const [siteTransfer, setSiteTransfer] = useState<SiteTransferInfo | null>(null);
  const [siteTransferSaving, setSiteTransferSaving] = useState(false);
  const [originalSiteTransfer, setOriginalSiteTransfer] = useState<SiteTransferInfo | null>(null);
  const [validationError, setValidationError] = useState<
    { field: string; depends: string; error: string }[] | null
  >(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSiteTransferSaved, setIsSiteTransferSaved] = useState<boolean>(false);

  const handleNoteChange = (content: string) => {
    const normalizedContent = content.trim();
    setNote(normalizedContent === '<p><br></p>' ? '' : normalizedContent);
  };

  const checkForValidation = (
    data: SiteTransferInfo
  ): { field: string; depends: string; error: string }[] | null => {
    const save_errors: { field: string; depends: string; error: string }[] = [];
    if (data.ProposalSubmitted === true && data.ProposalDate === null) {
      save_errors.push({
        field: 'ProposalDate',
        depends: 'ProposalSubmitted',
        error: 'Proposal Submission date is required.',
      });
    }
    if (data.LOISubmitted === true && data.LOIDate === null) {
      save_errors.push({
        field: 'LOIDate',
        depends: 'LOISubmitted',
        error: 'LOI Sumission date is required.',
      });
    }
    if (data.LOISigned === true && data.LOISignedDate === null) {
      save_errors.push({
        field: 'LOISignedDate',
        depends: 'LOISigned',
        error: 'LOI Sign date is required.',
      });
    }
    if (save_errors.length === 0) {
      return null;
    } else {
      return save_errors;
    }
  };

  const handleSaveSiteTransfer = async () => {
    if (siteTransfer && JSON.stringify(siteTransfer) !== JSON.stringify(originalSiteTransfer)) {
      setSiteTransferSaving(true);
      try {
        const validitionCheck = checkForValidation(siteTransfer);

        if (validitionCheck === null) {
          await saveSiteTransferDetails(siteTransfer);
          setOriginalSiteTransfer(deepCopy(siteTransfer));
          setValidationError(null);
          setIsSiteTransferSaved(true);
        } else {
          setValidationError(validitionCheck);
          // setSiteTransferSaving(false)
        }
      } catch (err) {
        console.error('Error saving site transfer details:', err);
        setIsSiteTransferSaved(false);
      } finally {
        setSiteTransferSaving(false);
      }
    } else if (validationError === null) {
      closeModal();
    }
  };

  const handleCloseSiteTransferModal = () => {
    if (JSON.stringify(siteTransfer) !== JSON.stringify(originalSiteTransfer)) {
      setIsConfirmModalOpen(true);
    } else {
      closeModal();
    }
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmModalOpen(false);
  };

  const handleConfirmForConfirmationModal = () => {
    setIsConfirmModalOpen(false);
    closeModal();
  };

  useEffect(() => {
    setValidationError(null);
    const controller = new AbortController();
    const signal = controller.signal;
    const handleOpenSiteTransfer = async (oppIID: number) => {
      try {
        const response = await getSiteTransfer(oppIID, signal);
        const fetchedData = response?.info || {
          ProposalSubmitted: false,
          ProposalDate: null,
          LOISubmitted: false,
          LOIDate: null,
          LOISigned: false,
          LOISignedDate: null,
          IsActive: null,
          OpportunityIterationID: oppIID,
        };
        setSiteTransfer(fetchedData);
        setOriginalSiteTransfer(fetchedData);
      } catch {
        // Error handling for site transfer fetch - handled silently
      }
    };
    handleOpenSiteTransfer(opportunityIterationID);

    return () => {
      controller.abort();
    };
  }, [opportunityIterationID, getSiteTransfer]);

  return {
    userModulesRoles,
    siteTransferLoading,
    siteTransferNotes,
    siteTransferNoteLoading,
    saveSiteTransferNote,
    setNote,
    note,
    handleNoteChange,
    siteTransfer,
    setSiteTransfer,
    handleSaveSiteTransfer,
    siteTransferSaving,
    validationError,
    setValidationError,
    isConfirmModalOpen,
    handleCloseConfirmationModal,
    handleCloseSiteTransferModal,
    handleConfirmForConfirmationModal,
    isSiteTransferSaved,
  };
};

export default SiteTransferLogic;
